import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useARC } from "providers/ARC/ARCContext";
import useFleetArcId from "lib/utils/useFleetArcId";
import { getFeatureFlagsForDeviceType } from "lib/consts/featureFlags";
import { useJetsonSystemStatus } from "lib/hooks/useJetsonSystemStatus";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import { probeObjectRecognitionService } from "lib/api/objectRecognition";
import { getRobotProfileBaseUrl, probeRobotProfileService } from "lib/robotProfileApi";

const DeviceFeatureFlagsContext = createContext(null);
DeviceFeatureFlagsContext.displayName = "DeviceFeatureFlags";

export const useDeviceFeatureFlags = () => {
    const context = useContext(DeviceFeatureFlagsContext);
    if (!context) {
        // Keep isolated component stories/tests usable without the route
        // provider, while all real control routes receive the live context.
        return {
            flags: getFeatureFlagsForDeviceType(undefined, { fleetMode: false }),
            deviceType: undefined,
            temperature: undefined,
            cpuPercent: undefined,
            hostname: undefined,
            statusResolved: false,
        };
    }
    return context;
};

const DeviceFeatureFlagsProvider = ({ children }) => {
    const arc = useARC();
    const fleetArcId = useFleetArcId();
    const arcHost = arc?.selectedARC?.host;
    const fleetMode = Boolean(fleetArcId);
    const isSecure = useIsHttpSecure();
    const status = useJetsonSystemStatus(arcHost);
    const [recognitionServiceAvailable, setRecognitionServiceAvailable] = useState(false);
    const [robotProfileServiceAvailable, setRobotProfileServiceAvailable] = useState(false);
    const robotProfileBaseUrl = useMemo(
        () => getRobotProfileBaseUrl(arcHost, isSecure, fleetArcId),
        [arcHost, fleetArcId, isSecure],
    );
    const deviceFlags = useMemo(
        () => getFeatureFlagsForDeviceType(status.deviceType, { fleetMode }),
        [fleetMode, status.deviceType],
    );

    useEffect(() => {
        let cancelled = false;
        setRecognitionServiceAvailable(false);

        if (!arcHost || String(arcHost).trim() === "") {
            return () => {
                cancelled = true;
            };
        }

        probeObjectRecognitionService(arcHost, isSecure, fleetArcId).then((available) => {
            if (!cancelled) setRecognitionServiceAvailable(available);
        }).catch(() => {
            if (!cancelled) setRecognitionServiceAvailable(false);
        });

        return () => {
            cancelled = true;
        };
    }, [arcHost, fleetArcId, isSecure]);

    useEffect(() => {
        let cancelled = false;
        setRobotProfileServiceAvailable(false);

        probeRobotProfileService(robotProfileBaseUrl).then((available) => {
            if (!cancelled) setRobotProfileServiceAvailable(available);
        }).catch(() => {
            if (!cancelled) setRobotProfileServiceAvailable(false);
        });

        return () => {
            cancelled = true;
        };
    }, [robotProfileBaseUrl]);

    const flags = useMemo(
        () => ({
            ...deviceFlags,
            HAS_OBJECT_RECOGNITION:
                deviceFlags.HAS_OBJECT_RECOGNITION || recognitionServiceAvailable,
            COMPLETE_ROBOT_PROFILE:
                deviceFlags.COMPLETE_ROBOT_PROFILE || robotProfileServiceAvailable,
        }),
        [deviceFlags, recognitionServiceAvailable, robotProfileServiceAvailable],
    );

    const value = useMemo(
        () => ({
            flags,
            deviceType: status.deviceType,
            temperature: status.temperature,
            cpuPercent: status.cpuPercent,
            hostname: status.hostname,
            statusResolved: status.statusResolved,
        }),
        [flags, status.cpuPercent, status.deviceType, status.hostname, status.statusResolved, status.temperature],
    );

    return (
        <DeviceFeatureFlagsContext.Provider value={value}>
            {children}
        </DeviceFeatureFlagsContext.Provider>
    );
};

export default DeviceFeatureFlagsProvider;
